import { GridColumn as Column } from '@progress/kendo-react-grid'
import * as Flash from 'components/flash'
import { HeaderComponent } from 'components/header/component'
import { SearchIcon } from 'components/icons/searchicon'
import { UpgradeAnchorLink } from 'components/link'
import { Loading } from 'components/loading'
import { RasaContext } from 'context'
import { AjaxWrapper, HttpMethod } from 'generic/ajaxWrapper'
import { EditableDropdownCell, EditCellProps} from 'generic/editCell'
import * as GenericRedux from 'generic/genericRedux'
import { PagingSource, RasaDataGrid } from 'generic/rasaDataGrid'
import { isTruthy } from 'generic/utility'
import * as GA from 'google-analytics'
import React, { Component } from 'react'
import { Button } from 'reactstrap'
import { SourceTypes, SubscriptionTypes } from 'shared_server_client/constants'
import * as BillingPlan from 'shared_server_client/types/billing_plan'
import './_styles.scss'
import * as Constants from './constants'
import {
  ActiveCell,
  BlankCell,
  DateTimeCell,
  NameCell,
  PreferredSourceCell,
  SourceIdentifierCell,
} from './kendocells'
import { SectionTitleCell } from './kendocells/sectionTitleCell'
import './styles.css'

type Source = any

interface SourcesState {
  accountSourcesCount: number,
  boostSourcesCount: number,
  canBoostSource: boolean,
  loading: boolean,
  maxBoostSource: number,
  plan?: BillingPlan.BillingPlan,
  search: string,
  sources: any[],
}

interface SourcesProps extends GenericRedux.DatasetContainerComponentProps<Source> {
  sourcesChanged: any,
}

export class SourcesListComponent extends Component<SourcesProps, SourcesState> {
  public static contextType = RasaContext;
  constructor(props: SourcesProps) {
    super(props);
    this.state = {
      accountSourcesCount: 0,
      boostSourcesCount: 0,
      canBoostSource: false,
      loading: true,
      maxBoostSource: 0,
      search: '',
      sources: [],
    }
    this.onBoostToggle = this.onBoostToggle.bind(this)
  }

  public componentDidMount() {
    this.context.user.init().then(({activeCommunity, person}) => {
      if (activeCommunity.billingInfo && activeCommunity.billingInfo.currentPlan) {
        this.setState({
          accountSourcesCount: activeCommunity.billingInfo.usageStats.sourcesCount,
          boostSourcesCount: activeCommunity.billingInfo.usageStats.boostSourcesCount,
          canBoostSource: activeCommunity.billingInfo.usageStats.canBoostSources,
          loading: false,
          maxBoostSource: activeCommunity.billingInfo.usageStats.currentPlanMaxBoostSources,
          plan: activeCommunity.billingInfo.currentPlan,
        })
      }
    })
  }

  public render() {
    if (this.state.loading) {
      return (<Loading size="64" />)
    } else {
      return (
        <div className="source-grid">
          <div className="header-container">
            <HeaderComponent
              icon={'sources'}
              title={'SOURCES'}
              subTitle={'All Sources'}
              description={[
                'Sources that will be checked to bring in new content for your newsletter.',
                'Boost a source to have all of its content included in every newsletter.',
              ]}
            />
            <this.sourcesLimitReached/>
          </div>
          <div>
            <div className="search-bar">
              <label>
                <SearchIcon/>
                <input type="text"
                      value={this.state.search}
                      placeholder="Search..."
                      onChange={(e) => this.setSearch(e.target.value)}/>
              </label>
            </div>
          </div>
          <RasaDataGrid<Source>
              entityName="communitySource"
              datasetName="communitySources"
              // props to pass along to underlying grid automatically
              editDefaultState={false}
              pagingSource={PagingSource.local}
              pageable={false}
              resizable={true}
              sortable={true}
              gridFilter={this.filterData}
              gridTransform={this.transformData}
              onRowClick={(e) => this.editSource(e.dataItem)}
              onDataReceived={this.dataReceived}
              data={[]}>
            <Column field="is_active" title="Allow" editor="boolean" cell={this.ToggleActiveCell} width="80%"/>
            <Column field="source_type" title="Source" editable={false}/>
            <Column field="name" title="Name" editable={false} cell={this.NameEditCell}/>
            <Column field="source_url" cell={SourceIdentifierCell} title="Source URL" editable={false} width="200%"/>
            <Column field="last_week_total" title="# Upcoming" editable={false} width="95%"/>
            <Column field="section_name" title="Section" editable={false} cell={this.SectionNameCell}/>
            <Column field="last_success" title="Last Fetch" editable={false} cell={DateTimeCell}/>
            <Column field="created" title="Created" editable={false} cell={DateTimeCell}/>
            <Column field="subscription_type_id" title="Boosted" cell={this.BoostedCell}/>
            <Column cell={this.SourceEditCell} width={50}/>
          </RasaDataGrid>
        </div>
      )
    }
  }

  protected BoostedCell = (props: EditCellProps) => {
    return props.dataItem.source_type !== SourceTypes.native ?
      <PreferredSourceCell
        boostSourcesCount={this.state.boostSourcesCount}
        maxBoostSource={this.state.maxBoostSource}
        onToggle={this.onBoostToggle}
       {...props} /> : <BlankCell />
  }
  protected transformData = (a) => {
    const input = this.state.sources.filter((x) => x.id === a.id && this.filterData(x))[0]
    input.is_active = a.is_active
    if (input.is_archived !== a.is_archived) {
      input.is_archived = a.is_archived
      input.is_active = !isTruthy(input.is_archived) ? true : a.is_active
    }
    return input
  }
  protected onBoostToggle = (props: EditCellProps, newBoostValue) => {
    let sourcesList = this.state.sources.map((x) => x)
    if (this.state.maxBoostSource === 1) {
      sourcesList = sourcesList.map((x) => {
        return {
          ...x,
          subscription_type_id: SubscriptionTypes.normal,
        }
      })
    }
    sourcesList = sourcesList.map((x) => {
      return {
        ...x,
        subscription_type_id: props.dataItem.id === x.id ? newBoostValue : x.subscription_type_id,
      }
    })
    this.setState({
      sources: sourcesList,
      boostSourcesCount: newBoostValue === SubscriptionTypes.lead ? this.state.boostSourcesCount + 1 :
        this.state.boostSourcesCount - 1,
    })
  }
  protected ToggleActiveCell = (props: EditCellProps) => {
    return props.dataItem.source_type !== SourceTypes.native ?
      <ActiveCell {...props} /> : <BlankCell />
  }

  protected SourceEditCell = (props: EditCellProps) => {
    return props.dataItem.source_type !== SourceTypes.native ? <EditableDropdownCell {...props}
      onEdit={this.editSource}
      onRefresh={this.refreshSource}
      canRefresh={isTruthy(props.dataItem.is_active) && props.dataItem.source_type !== SourceTypes.native}
      canArchive={true}
      canRestore={true}/> : <BlankCell />
  }

  protected NameEditCell = (props: EditCellProps) => {
    return <NameCell {...props} onClick={this.editSource} />
  }

  protected SectionNameCell = (props: EditCellProps) => {
    return <SectionTitleCell {...props} />
  }

  protected dataReceived = (event) => {
    if (event.data) {
      const includedSources = event.data.filter((r) => r.source_type !== SourceTypes.native)
      const manualSources = event.data.filter((r) => r.source_type === SourceTypes.native)
      const manualSource = manualSources.reduce((result, x) => {
        return {
          ...result,
          ...x,
          last_run: x.last_run ? new Date(Math.max.apply(null, [result.last_run, x.last_run])) : result.last_run,
          last_week_total: result.last_week_total + x.last_week_total,
          last_week_quality: result.last_week_total + x.last_week_quality,
        }
      }, {
        last_run: null,
        last_week_total: 0,
        last_week_quality: 0,
      })
      event.data = [manualSource].concat(includedSources)
      this.setState({
        sources: event.data,
      })
    }
  }

  protected filterData = (s: Source): boolean => {
    return this.state.search.length <= 3 || (
      (s.name && s.name.indexOf(this.state.search) >= 0) ||
      (s.source_url && s.source_url.indexOf(this.state.search) >= 0)
    )
  }

  protected setSearch = (s: string) => {
    this.setState({
      search: s,
    })
  }

  protected sourceUrl = (s: Source) => `/content/editsource?id=${s.id}`

  protected editSource = (s: Source) => !s.is_archived && this.props.push(this.sourceUrl(s))

  protected refreshSource = (s: Source) => {
    this.setState({loading: true}, () =>
      AjaxWrapper.ajax(AjaxWrapper.getServerUrl() + `/content/sources/refresh`,
        HttpMethod.POST,
        s,
      )
      .then(() => {
        this.setState({
          loading: false,
        }, () => this.context.store.dispatch(Flash.showFlashMessage(Constants.SOURCE_REFRESHED)))
      })
      .catch((error) => this.setState({loading: false})),
    )
  }

  protected sourcesLimitReached = () => {
    const maxSourceCount = BillingPlan.getCurrentPlanMaxSources(this.state.plan)
    if ( this.state.accountSourcesCount >= maxSourceCount ) {
      return (
        <div className="max-sources-container">
          <div className="add-sources-button-text-section">
            <Button className="add-sources-button disabled">
              <span className="sources-button-text">
                Add New Source
              </span>
            </Button>
          </div>
          <div className="new-sources-wrapper">
            <div className="new-sources-wrapper-text">
            You've reached your limit of <b>{maxSourceCount} sources</b>
            </div>
            <UpgradeAnchorLink source={GA.UpgradeSource.AddSource}>
              Upgrade
            </UpgradeAnchorLink> to unlock more
            </div>
        </div>)
    }
    return (
    <div className="add-sources-button-text-section">
      <Button className="add-button" onClick={() => this.props.push(Constants.CONTENT_ADD_SOURCE_PAGE)}>
        <span className="button-text">
          Add New Source
        </span>
        </Button>
    </div>)
  }
}

//tslint:disable-next-line
export const ContentPoolSourcesList = GenericRedux.registerNewDatasetContainerComponent<Source>(
  SourcesListComponent, 'sources')
